/**
 * Audit log — Phase 5. Paginated operator trail with filters.
 */

import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import { listAuditLog } from "@/lib/api";
import { formatDateTime, formatRelative } from "@/lib/format";
import type { AuditLogRow } from "@/lib/types";

function actionTone(action: string): string {
  if (action.startsWith("device.")) {
    return "text-cyan-200/90";
  }
  if (action.startsWith("release.")) {
    return "text-orange-200/90";
  }
  if (action.startsWith("stripe.")) {
    return "text-violet-200/90";
  }
  if (action.startsWith("sim.")) {
    return "text-sky-200/90";
  }
  return "text-brand-dim";
}

export default function AuditLog() {
  const [draft, setDraft] = useState({
    operator: "",
    action: "",
    target: "",
    since: "",
    until: "",
  });
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const queryKey = useMemo(
    () => ["audit", applied, page, pageSize] as const,
    [applied, page],
  );

  const auditQ = useQuery({
    queryKey,
    queryFn: () =>
      listAuditLog({
        operator: applied.operator.trim() || undefined,
        action: applied.action.trim() || undefined,
        target: applied.target.trim() || undefined,
        since: applied.since.trim() || undefined,
        until: applied.until.trim() || undefined,
        page,
        limit: pageSize,
      }),
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Stripe webhooks, releases, SIM actions, scheduled workers, and manual admin changes."
      />
      <div className="px-8 py-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-surface-border bg-surface px-4 py-3">
          <FilterField
            label="Operator"
            value={draft.operator}
            onChange={(v) => setDraft((d) => ({ ...d, operator: v }))}
          />
          <FilterField
            label="Action"
            value={draft.action}
            onChange={(v) => setDraft((d) => ({ ...d, action: v }))}
          />
          <FilterField
            label="Target"
            value={draft.target}
            onChange={(v) => setDraft((d) => ({ ...d, target: v }))}
          />
          <FilterField
            label="Since (ISO)"
            value={draft.since}
            onChange={(v) => setDraft((d) => ({ ...d, since: v }))}
            mono
          />
          <FilterField
            label="Until (ISO)"
            value={draft.until}
            onChange={(v) => setDraft((d) => ({ ...d, until: v }))}
            mono
          />
          <button
            type="button"
            onClick={() => {
              setApplied({ ...draft });
              setPage(1);
            }}
            className="rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              const empty = {
                operator: "",
                action: "",
                target: "",
                since: "",
                until: "",
              };
              setDraft(empty);
              setApplied(empty);
              setPage(1);
            }}
            className="text-xs uppercase tracking-wider text-brand-dim hover:text-slate-200"
          >
            Clear
          </button>
        </div>

        <div className="rounded-md border border-surface-border bg-surface overflow-hidden">
          {auditQ.isLoading ? (
            <LoadingBlock label="Loading audit log…" />
          ) : auditQ.isError ? (
            <ErrorBlock error={auditQ.error} onRetry={() => auditQ.refetch()} />
          ) : !auditQ.data || auditQ.data.audit.length === 0 ? (
            <EmptyBlock title="No rows match these filters." />
          ) : (
            <>
              <div className="px-4 py-2 text-xs text-brand-dim border-b border-surface-border">
                Page {auditQ.data.page} of {auditQ.data.total_pages} · {auditQ.data.total}{" "}
                total
              </div>
              <AuditTable rows={auditQ.data.audit} />
              <div className="flex items-center justify-between px-4 py-3 border-t border-surface-border">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-sm text-brand-orange disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= auditQ.data.total_pages}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-sm text-brand-orange disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function FilterField({
  label,
  value,
  onChange,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 min-w-[140px]">
      <label className="text-[11px] uppercase tracking-wider text-brand-dim">
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange ${mono ? "font-mono text-xs" : ""}`}
      />
    </div>
  );
}

function AuditTable({ rows }: { rows: AuditLogRow[] }) {
  const [openId, setOpenId] = useState<number | null>(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
          <tr>
            <th className="px-4 py-2 font-medium">When</th>
            <th className="px-4 py-2 font-medium">Operator</th>
            <th className="px-4 py-2 font-medium">Action</th>
            <th className="px-4 py-2 font-medium">Target</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {rows.map((r) => (
            <AuditRow
              key={r.id}
              row={r}
              open={openId === r.id}
              onToggle={() => setOpenId(openId === r.id ? null : r.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditRow({
  row,
  open,
  onToggle,
}: {
  row: AuditLogRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <Fragment>
      <tr
        className="hover:bg-surface-raised/60 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-2 text-slate-200 whitespace-nowrap" title={formatDateTime(row.at)}>
          {formatRelative(row.at)}
        </td>
        <td className="px-4 py-2 text-slate-300 text-xs">{row.operator_email}</td>
        <td className={`px-4 py-2 text-xs font-mono ${actionTone(row.action)}`}>
          {row.action}
        </td>
        <td className="px-4 py-2 font-mono text-xs text-slate-300">{row.target ?? "—"}</td>
      </tr>
      {open ? (
        <tr className="bg-surface-sunken/40">
          <td colSpan={4} className="px-4 py-3">
            <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
              {row.payload ? JSON.stringify(row.payload, null, 2) : "—"}
            </pre>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}
